import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faAngleDoubleUp, 
  faCommentDots, 
  faTimes, 
  faArrowUp,
  faPaperPlane,
  faRobot
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './BottomCallAction.css';

const BottomCallAction = () => {
  return (
    <div className="bottom-call-actions">
      <ButtonScrollUp />
      <WhatsAppChat />
    </div>
  );
};

const ButtonScrollUp = () => {
  const [isVisible, setIsVisible] = useState(false);

  const handleScroll = () => {
    if (typeof window !== 'undefined') {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsVisible(scrollTop > 200);
    }
  };

  const scrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <>
      {isVisible && (
        <button
          className="scroll-top-button"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          title="Scroll to top"
        >
          <FontAwesomeIcon icon={faAngleDoubleUp} className="scroll-icon" />
        </button>
      )}
    </>
  );
};

const WhatsAppChat = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    { 
      text: "Hello! I'm your Robomind Academy Assistant. How can I help you today?", 
      sender: "system",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

const WHATSAPP_NUMBER = process.env.REACT_APP_MOBILE_NUMBER1;
  const AI_RESPONSE_DELAY = 1000;
  const WHATSAPP_REDIRECT_MESSAGE = "I'm interested in learning more about Robomind Academy. Can you help me?";

  const quickQuestions = [
    "What age group can join?",
    "What courses do you offer?",
    "Is the schedule flexible?",
    "Are sessions one-on-one?",
    "How to book a free session?"
  ];
  
  const aiResponses = {
    default: "For more detailed information, please contact us directly via WhatsApp by clicking the button below.",
  
    "What age group can join?": "We accept students from 6 to 17 years old.",
  
    "What courses do you offer?": "We offer game development, mobile apps, web development, Python, Java, and more. You can check our courses section for full details.",
  
    "Is the schedule flexible?": "Yes, we offer a flexible schedule based on your preferred time and days.",
  
    "Are sessions one-on-one?": "Yes, all our sessions are private and one-on-one. Each student has their own teacher.",
  
    "How to book a free session?": "Click on 'Book a Free Session' and choose your preferred date and time. It's completely free."
  };

  useEffect(() => {
    const storedMessages = localStorage.getItem("chatMessages");
    if (storedMessages) {
      setMessages(JSON.parse(storedMessages));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("chatMessages", JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    //messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToTop = () => {
    messagesContainerRef.current?.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const handleScroll = () => {
    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      const { scrollTop } = messagesContainer;
      setShowScrollUp(scrollTop < -50);
    }
  };

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen) {
      setTimeout(scrollToBottom, 100);
    }
  };

  const handleQuickQuestionClick = (question) => {
    const userMessage = {
      text: question,
      sender: "user",
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    setTimeout(() => {
      const response = aiResponses[question] || aiResponses.default;
      const aiResponse = {
        text: response,
        sender: "system",
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, AI_RESPONSE_DELAY);
  };

  const redirectToWhatsApp = (message = WHATSAPP_REDIRECT_MESSAGE) => {
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    const redirectMessage = {
      text: "I've opened WhatsApp for you to continue with our team!",
      sender: "system",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, redirectMessage]);
  };

  const sendMessage = () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      text: inputMessage,
      sender: "user",
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsTyping(true);

    setTimeout(() => {
      let response = aiResponses.default;
      for (const [question, answer] of Object.entries(aiResponses)) {
        if (inputMessage.toLowerCase().includes(question.toLowerCase().replace("?", ""))) {
          response = answer;
          break;
        }
      }
      
      const aiResponse = {
        text: response,
        sender: "system",
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);

      if (response === aiResponses.default) {
        redirectToWhatsApp(inputMessage);
      }
    }, AI_RESPONSE_DELAY);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      sendMessage();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      <button
        className={`chat-button ${isChatOpen ? "open" : ""}`}
        onClick={toggleChat}
        aria-label="Chat with us"
      >
        {isChatOpen ? (
          <FontAwesomeIcon icon={faTimes} />
        ) : (
          <>
            <FontAwesomeIcon icon={faCommentDots} />
            <span className="chat-button-text">Chat with us</span>
          </>
        )}
      </button>

      <div
        className={`chat-window ${isChatOpen ? "visible" : ""}`}
        style={{
          width: isChatOpen && window.innerWidth > 768 ? "380px" : "320px",
        }}
      >
        <div className="chat-header">
          <div className="chat-title">
            <FontAwesomeIcon icon={faRobot} className="chat-icon" />
            <div>
              <div className="chat-main-title">Robomind Assistant</div>
              <div className="chat-subheader">Ask me anything about our academy!</div>
            </div>
          </div>
        </div>

        <div 
          className="chat-messages" 
          ref={messagesContainerRef}
          onScroll={handleScroll}
        >
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`chat-message ${msg.sender === "user" ? "user" : "system"}`}
            >
              <div className="message-content">
                {msg.text.split('\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
                <div className="message-time">{formatTime(new Date(msg.timestamp))}</div>
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="chat-message system">
              <div className="message-content typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          {/* Always show quick questions at the bottom */}
          {isChatOpen && (
            <div className="quick-questions-container">
              <div className="quick-questions-title">Quick questions:</div>
              <div className="quick-questions-grid">
                {quickQuestions.map((question, index) => (
                  <button
                    key={index}
                    className="quick-question"
                    onClick={() => handleQuickQuestionClick(question)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
          
          {showScrollUp && (
            <button 
              className="scroll-up-button" 
              onClick={scrollToTop}
              aria-label="Scroll to top"
            >
              <FontAwesomeIcon icon={faArrowUp} />
            </button>
          )}
        </div>

        <div className="chat-input-container">
          <div className="chat-input">
            <input
              type="text"
              placeholder="Type your question..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button 
              onClick={sendMessage}
              aria-label="Send message"
              disabled={!inputMessage.trim()}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
          <button 
            className="whatsapp-redirect-button"
            onClick={() => redirectToWhatsApp()}
          >
            <FontAwesomeIcon icon={faWhatsapp} />
            <span>Continue on WhatsApp</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default BottomCallAction;